import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CoreService } from '../../../services/core.service';
import {
  LatestPost,
  LatestPostsComponent,
} from '../../sections/latest-posts/latest-posts.component';
import {
  LatestAlert,
  LatestAlertsComponent,
} from '../../sections/latest-alerts/latest-alerts.component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    LatestPostsComponent,
    LatestAlertsComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() maxCards = 3;
  @Input() naPlaceholder = './assets/images/na.png';

  posts: LatestPost[] = [];
  alerts: LatestAlert[] = [];

  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';

  private destroy$ = new Subject<void>();

  private readonly onStorage = (e: StorageEvent) => {
    if (e.key === this.LATEST_POSTS_KEY || e.key === this.LATEST_ALERTS_KEY) {
      this.loadFromStorage();
    }
  };

  constructor(
    private router: Router,
    private coreService: CoreService,
  ) {}

  ngOnInit(): void {
    this.loadFromStorage();

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorage);
    }

    this.coreService.latestPosts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(posts => {
        if (posts && posts.length > 0) {
          this.posts = posts.slice(0, this.maxCards);
        }
      });

    this.coreService.latestAlerts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(alerts => {
        if (alerts && alerts.length > 0) {
          this.alerts = alerts.slice(0, this.maxCards);
        }
      });
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onStorage);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadFromStorage(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      this.alerts = [];
      return;
    }

    this.loadPostsFromStorage();
    this.loadAlertsFromStorage();
  }

  private loadPostsFromStorage(): void {
    try {
      const rawPosts = localStorage.getItem(this.LATEST_POSTS_KEY);
      const parsedPosts = rawPosts
        ? JSON.parse(rawPosts) as LatestPost[] | null
        : null;

      this.posts = Array.isArray(parsedPosts)
        ? parsedPosts.slice(0, this.maxCards)
        : [];
    } catch {
      this.posts = [];
    }
  }

  private loadAlertsFromStorage(): void {
    try {
      const rawAlerts = localStorage.getItem(this.LATEST_ALERTS_KEY);
      const parsedAlerts = rawAlerts
        ? JSON.parse(rawAlerts) as LatestAlert[] | null
        : null;

      this.alerts = Array.isArray(parsedAlerts)
        ? parsedAlerts.slice(0, this.maxCards)
        : [];
    } catch {
      this.alerts = [];
    }
  }

  viewRow(row: LatestPost): void {
    const targetId =
      row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;

    this.router.navigate(['/preview-post', row.tag ?? '', targetId]);
  }

  viewAlert(alert: LatestAlert): void {
    this.router.navigate([
      '/preview-post',
      alert.format,
      alert.posting_queue_id,
    ]);
  }
}